import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Component, forwardRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChip, MatChipRemove, MatChipSet } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, SubscriptSizing } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { ChipMultiselectOption } from '../../../interfaces/chip-multiselect-option';

@Component({
    selector: 'kh-chip-multiselect',
    templateUrl: './chip-multiselect.component.html',
    styleUrls: ['./chip-multiselect.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipMultiselectComponent),
            multi: true
        }
    ],
    imports: [
        FormsModule,
        MatChip,
        MatChipRemove,
        MatChipSet,
        MatDivider,
        MatFormField,
        MatIcon,
        MatOption,
        MatSelect,
        MatSelectTrigger
    ]
})

export class ChipMultiselectComponent implements ControlValueAccessor, OnChanges {
    @ViewChild(MatSelect) private _select: MatSelect | undefined;

    @Input() options: ChipMultiselectOption[] = [];
    @Input() placeholder = '';
    @Input() selectAllOptionId: string | null = null;
    @Input() subscriptSizing: SubscriptSizing = 'fixed';
    @Input() summaryOptionId: string | null = null;

    private _previousValue: any[] = [];

    isDisabled = false;
    orderedOptions: ChipMultiselectOption[] = [];
    value: any[] = [];

    onChange: (value: any[]) => void = () => { };
    onTouched: () => void = () => { };

    get displayedChipValues(): any[] {
        if (this.selectAllOptionId !== null && this.value.includes(this.selectAllOptionId)) {
            return [this.selectAllOptionId];
        }

        return this.value;
    }

    private get individualOptionIds(): any[] {
        return this.options.filter(option => option.id !== this.selectAllOptionId).map(option => option.id);
    }

    getOptionText(value: any): string {
        const option = this.options.find((item) => item.id === value);
        return option ? option.text : '';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['options'] || changes['summaryOptionId'] || changes['selectAllOptionId']) {
            this.orderedOptions = this.orderOptions();
        }
        if (changes['options']) {
            this.expandSelectAll();
            this._previousValue = [...this.value];
        }
    }

    onSelectionChange(): void {
        this.value = this.reconcileSelectAll(this.value);
        this._previousValue = [...this.value];
        this.onChange(this.value);
        this.onTouched();
        this.repositionPanel();
    }

    registerOnChange(fn: (value: any[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    remove(value: any): void {
        this.value = this.reconcileSelectAll(this.value.filter((item) => item !== value));
        this._previousValue = [...this.value];
        this.onChange(this.value);
        this.onTouched();
        this.repositionPanel();
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    writeValue(value: any[]): void {
        this.value = value ?? [];
        this.expandSelectAll();
        this._previousValue = [...this.value];
    }

    private expandSelectAll(): void {
        if (this.selectAllOptionId === null || !this.value.includes(this.selectAllOptionId)) {
            return;
        }

        this.value = [this.selectAllOptionId, ...this.individualOptionIds];
    }

    private orderOptions(): ChipMultiselectOption[] {
        const summaryOption = this.options.find(option => option.id === this.summaryOptionId);
        if (!summaryOption) {
            return this.options;
        }

        const selectAllOption = this.options.find(option => option.id === this.selectAllOptionId);
        const remainingOptions = this.options.filter(option => option !== summaryOption && option !== selectAllOption);

        return selectAllOption
            ? [selectAllOption, summaryOption, ...remainingOptions]
            : [summaryOption, ...remainingOptions];
    }

    private reconcileSelectAll(newValue: any[]): any[] {
        if (this.selectAllOptionId === null) {
            return newValue;
        }

        const individualOptionIds = this.individualOptionIds;
        const hasSelectAll = newValue.includes(this.selectAllOptionId);
        const hadSelectAll = this._previousValue.includes(this.selectAllOptionId);
        if (hasSelectAll && !hadSelectAll) {
            return [this.selectAllOptionId, ...individualOptionIds];
        }
        if (!hasSelectAll && hadSelectAll && individualOptionIds.every(id => newValue.includes(id))) {
            return [];
        }
        if (hasSelectAll && hadSelectAll) {
            return newValue.filter(item => item !== this.selectAllOptionId);
        }

        return newValue;
    }

    // Angular Material computes the select overlay position only when the panel
    // opens; it does not track trigger size changes. Because the selected chips
    // live inside the trigger, selecting or removing an option resizes the
    // trigger and leaves the open panel misaligned. MatSelect exposes no public
    // API for this, so reach its internal CdkConnectedOverlay and recompute the
    // position once the DOM has settled at its new height.
    private repositionPanel(): void {
        setTimeout((): void => {
            const overlayDir = (this._select as unknown as { _overlayDir?: CdkConnectedOverlay; })._overlayDir;
            overlayDir?.overlayRef?.updatePosition();
        });
    }
}
